import useReveal from "@/hooks/useReveal";
import { PRIMARY_SHOPIFY_URL } from "@/lib/shopify";

const PRODUCT_IMAGE =
  "https://customer-assets.emergentagent.com/job_afrqa-luxury/artifacts/0esx8z7g_fcda3137-3915-43e7-b3f9-6b5055ad7dbd.JPG";

export default function Hero() {
  const headline = useReveal();
  const cta = useReveal();
  const img = useReveal();

  return (
    <section
      data-testid="hero-section"
      className="relative w-full min-h-[100svh] overflow-hidden bg-[#070707]"
    >
      {/* Ambient gold + sand glow on matte black */}
      <div
        aria-hidden
        className="absolute -right-40 top-1/4 w-[680px] h-[680px] rounded-full pointer-events-none"
        style={{
          background:
            "radial-gradient(circle, rgba(212,175,55,0.16) 0%, rgba(230,213,184,0.06) 35%, transparent 70%)",
          filter: "blur(20px)",
        }}
      />
      <div
        aria-hidden
        className="absolute -left-32 bottom-0 w-[460px] h-[460px] rounded-full pointer-events-none"
        style={{
          background:
            "radial-gradient(circle, rgba(212,175,55,0.08) 0%, transparent 70%)",
          filter: "blur(20px)",
        }}
      />
      <div className="grain-overlay" />

      {/* Vertical brand mark — left rail */}
      <div className="hidden lg:flex absolute left-10 top-1/2 -translate-y-1/2 flex-col items-center gap-4 z-10">
        <div className="w-px h-16 bg-[#d4af37]/40" />
        <span
          className="font-label text-[#d4af37] [writing-mode:vertical-rl]"
          style={{ transform: "rotate(180deg)" }}
        >
          Edition 001 · Gold
        </span>
        <div className="w-px h-16 bg-[#d4af37]/40" />
      </div>

      <div className="relative z-[1] max-w-[1400px] mx-auto px-6 md:px-10 pt-32 md:pt-40 pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          {/* Copy */}
          <div className="lg:col-span-6 order-2 lg:order-1">
            <div
              ref={headline.ref}
              className={`reveal ${headline.inView ? "in-view" : ""}`}
            >
              <div className="flex items-center gap-3 mb-8">
                <span className="pulse-dot" />
                <span className="font-label text-[#d4af37]">
                  AFRQA™ — The Africa Frame
                </span>
              </div>
              <h1 className="font-display text-[clamp(2.8rem,7.5vw,6.6rem)] leading-[0.96] tracking-[-0.025em] text-white">
                The
                <br />
                <span className="font-editorial italic gold-text-gradient">
                  Africa
                </span>{" "}
                Frame
                <span className="gold text-[0.4em] align-super ml-1">™</span>
                <br />
                <span className="font-editorial italic text-[#e6d5b8] text-[0.62em] leading-none">
                  Gold Edition.
                </span>
              </h1>
              <p
                className="mt-8 max-w-md text-[#d4d4cf] text-lg leading-relaxed font-light"
                data-testid="hero-subtitle"
              >
                Hand-formed in the silhouette of the continent. Limited to{" "}
                <span className="text-white">300</span> worldwide.
              </p>
            </div>

            <div
              ref={cta.ref}
              className={`reveal reveal-delay-2 ${
                cta.inView ? "in-view" : ""
              } mt-10 flex flex-wrap items-center gap-5`}
            >
              <a
                href={PRIMARY_SHOPIFY_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-preorder-btn"
                className="btn-gold"
              >
                Preorder for $99
                <span aria-hidden>→</span>
              </a>
              <a
                href="#cinematic"
                data-testid="hero-watch-film-btn"
                className="btn-ghost-gold"
              >
                <span
                  aria-hidden
                  className="inline-block w-2.5 h-2.5 bg-[#d4af37]"
                />
                Watch the Film
              </a>
            </div>

            {/* Press-style stats line */}
            <div className="mt-14 grid grid-cols-3 max-w-md gap-6 border-t border-[#1f1f1f] pt-8">
              <div>
                <p className="font-editorial italic gold-text-gradient text-3xl leading-none">
                  300
                </p>
                <span className="font-label text-[#8a8a85] mt-2 block">
                  Pieces
                </span>
              </div>
              <div>
                <p className="font-editorial italic gold-text-gradient text-3xl leading-none">
                  001
                </p>
                <span className="font-label text-[#8a8a85] mt-2 block">
                  Edition
                </span>
              </div>
              <div>
                <p className="font-editorial italic gold-text-gradient text-3xl leading-none">
                  18k
                </p>
                <span className="font-label text-[#8a8a85] mt-2 block">
                  Gold-tone
                </span>
              </div>
            </div>
          </div>

          {/* Product */}
          <div
            ref={img.ref}
            className={`reveal reveal-delay-1 ${
              img.inView ? "in-view" : ""
            } lg:col-span-6 order-1 lg:order-2 relative`}
          >
            {/* Editorial frame label */}
            <div className="absolute -top-6 left-0 right-0 flex items-center justify-between font-label text-[#8a8a85] z-10">
              <span>Vol. I · The Africa Frame™</span>
              <span className="text-[#d4af37]">001 / 300</span>
            </div>

            <div className="relative">
              {/* Gold corner marks */}
              <span
                aria-hidden
                className="absolute -top-2 -left-2 w-6 h-6 border-l border-t border-[#d4af37]"
              />
              <span
                aria-hidden
                className="absolute -top-2 -right-2 w-6 h-6 border-r border-t border-[#d4af37]"
              />
              <span
                aria-hidden
                className="absolute -bottom-2 -left-2 w-6 h-6 border-l border-b border-[#d4af37]"
              />
              <span
                aria-hidden
                className="absolute -bottom-2 -right-2 w-6 h-6 border-r border-b border-[#d4af37]"
              />

              <div className="zoom-wrap prototype-photo bg-[#0c0c0c] aspect-square overflow-hidden">
                <img
                  src={PRODUCT_IMAGE}
                  alt="The Africa Frame Gold Edition — front view, real prototype"
                  className="w-full h-full object-cover hero-image"
                  data-testid="hero-product-image"
                />
                <span
                  aria-hidden
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background:
                      "radial-gradient(ellipse at center, transparent 55%, rgba(0,0,0,0.42) 100%)",
                  }}
                />
              </div>
            </div>

            {/* Prototype tag */}
            <div className="mt-5 flex items-center gap-3 text-[#9a9a93]">
              <span className="pulse-dot" />
              <span className="font-label text-[#d4af37]">
                Official Prototype Complete
              </span>
              <span className="font-label text-[#6a6a65] hidden sm:inline">
                · Real photography
              </span>
            </div>

            {/* Price tag */}
            <div className="mt-6 flex items-end justify-between">
              <div>
                <span className="font-label text-[#8a8a85]">Edition Price</span>
                <p className="font-display text-white text-4xl mt-1">
                  $99{" "}
                  <span className="text-[#8a8a85] text-base">USD</span>
                </p>
              </div>
              <p className="font-editorial italic text-[#d4d4cf] text-right max-w-[12rem]">
                Hand-finished
                <br />
                <span className="gold-text-gradient">in limited edition.</span>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll hint */}
      <div
        className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-3 text-[#9a9a93]"
        aria-hidden
      >
        <span className="font-label text-[10px]">Scroll</span>
        <div className="hairline" />
      </div>
    </section>
  );
}
